import { useState, useEffect } from 'react'
import ManagerMobileLayout from '../../components/ManagerMobileLayout'
import api from '../../lib/api'

interface Absence {
  id: string
  status: string
  startDate: string
  endDate: string
  durationDays: number
  note?: string
  refusalReason?: string
  employee: { firstName: string; lastName: string }
  absenceType: { name: string; code: string; color: string }
}

const STATUS_COLORS: Record<string, string> = {
  pending:  'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  refused:  'bg-red-100 text-red-700'
}
const STATUS_LABELS: Record<string, string> = { pending: 'En attente', approved: 'Approuvé', refused: 'Refusé' }

function fmt(d: string) {
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

export default function ManagerAbsences() {
  const [absences, setAbsences] = useState<Absence[]>([])
  const [filter, setFilter] = useState<'pending' | 'all'>('pending')
  const [loading, setLoading] = useState(true)
  const [refusalModal, setRefusalModal] = useState<{ id: string } | null>(null)
  const [refusalReason, setRefusalReason] = useState('')

  useEffect(() => { load() }, [filter])

  async function load() {
    setLoading(true)
    const q = filter === 'pending' ? '?status=pending' : ''
    const r = await api.get(`/absences${q}`)
    setAbsences(r.data)
    setLoading(false)
  }

  async function approve(id: string) {
    await api.put(`/absences/${id}`, { status: 'approved' })
    await load()
  }

  async function refuse(id: string) {
    await api.put(`/absences/${id}`, { status: 'refused', refusalReason })
    setRefusalModal(null)
    setRefusalReason('')
    await load()
  }

  return (
    <ManagerMobileLayout>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-800">Congés & Absences</h1>
          <div className="flex bg-gray-100 rounded-lg p-1 text-xs">
            <button onClick={() => setFilter('pending')}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${filter === 'pending' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
              En attente
            </button>
            <button onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${filter === 'all' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
              Toutes
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : absences.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm text-gray-400">
            {filter === 'pending' ? 'Aucune demande en attente' : 'Aucune absence'}
          </div>
        ) : (
          <div className="space-y-3">
            {absences.map(a => (
              <div key={a.id} className="bg-white rounded-2xl shadow-sm p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-gray-800">{a.employee.firstName} {a.employee.lastName !== '-' ? a.employee.lastName : ''}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: a.absenceType.color }}>
                        {a.absenceType.code}
                      </span>
                      <span className="text-sm text-gray-600">{fmt(a.startDate)} → {fmt(a.endDate)}</span>
                      <span className="text-xs text-gray-400">{a.durationDays}j</span>
                    </div>
                    {a.note && <p className="text-xs text-gray-400 italic mt-1">{a.note}</p>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[a.status]}`}>
                    {STATUS_LABELS[a.status]}
                  </span>
                </div>
                {a.status === 'pending' && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => approve(a.id)}
                      className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-medium transition-colors">
                      ✓ Approuver
                    </button>
                    <button onClick={() => setRefusalModal({ id: a.id })}
                      className="flex-1 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-medium transition-colors">
                      ✗ Refuser
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refusal modal */}
      {refusalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white rounded-t-2xl w-full p-6 space-y-3">
            <h3 className="font-semibold text-gray-800">Motif de refus</h3>
            <textarea value={refusalReason} onChange={e => setRefusalReason(e.target.value)}
              placeholder="Expliquez la raison du refus..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300" />
            <div className="flex gap-3">
              <button onClick={() => setRefusalModal(null)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 font-medium text-sm">
                Annuler
              </button>
              <button onClick={() => refuse(refusalModal.id)}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium text-sm">
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}
    </ManagerMobileLayout>
  )
}
